import { MESES_HISTORICO_REVISAO, type JanelaHistorico } from "@/api/metas.service";

export function moeda(reais: number) {
  return reais.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
}

/**
 * Arredonda para cima ao real inteiro (9,40 → 10). Passa antes pelo centavo
 * para que o ruído de ponto flutuante (10,000000001) não vire R$ 11.
 */
export function reaisInteiros(valor: number) {
  return Math.ceil(Math.round(valor * 100) / 100);
}

/** Rótulo de um mês, ex.: "outubro de 2026". */
export function rotuloMes(ano: number, mes: number) {
  return new Date(ano, mes - 1, 1).toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
}

/** Chave "AAAA-MM" usada como índice de mês nos formulários que trabalham com uma lista de meses (Cenários, Feriados). */
export const CHAVE_MES = /^\d{4}-\d{2}$/;

export function chaveDe(ano: number, mes: number) {
  return `${ano}-${String(mes).padStart(2, "0")}`;
}

export function partesDe(chave: string) {
  const [ano = 0, mes = 1] = chave.split("-").map(Number);
  return { ano, mes };
}

/** Rótulo de uma chave "AAAA-MM", ou "—" se a chave for inválida/vazia. */
export function rotuloMesChave(chave: string) {
  if (!CHAVE_MES.test(chave)) return "—";
  const { ano, mes } = partesDe(chave);
  return rotuloMes(ano, mes);
}

/**
 * Os meses fechados aceitos como período histórico, do mais recente ao mais
 * antigo: do mês anterior a hoje até janeiro do ano anterior. Mesma janela
 * que PKG_FLASH_META_CALC.CALCULAR valida (app-dono-modulos
 * migrations/C5_big/20260828000010, erro -20013) — relativa a hoje, não ao
 * mês-alvo de quem a usa (cenário ou evento).
 */
export function mesesPermitidos(): string[] {
  const hoje = new Date();
  const anoMinimo = hoje.getFullYear() - 1;
  const meses: string[] = [];
  const cursor = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
  while (cursor.getFullYear() >= anoMinimo) {
    meses.push(chaveDe(cursor.getFullYear(), cursor.getMonth() + 1));
    cursor.setMonth(cursor.getMonth() - 1);
  }
  return meses;
}

/** Os MESES_HISTORICO_REVISAO meses fechados antes de ano/mês, do mais antigo ao mais recente — a coluna "meses anteriores". */
export function mesesAnteriores(ano: number, mes: number) {
  return Array.from({ length: MESES_HISTORICO_REVISAO }, (_, i) => {
    const data = new Date(ano, mes - 1 - (MESES_HISTORICO_REVISAO - i), 1);
    return { ano: data.getFullYear(), mes: data.getMonth() + 1 };
  });
}

/** A janela de `mesesAnteriores` como o ERP a recebe: do dia 1 do primeiro mês ao último dia do último. */
export function janelaHistorico(ano: number, mes: number): JanelaHistorico {
  const meses = mesesAnteriores(ano, mes);
  const primeiro = meses[0]!;
  const ultimo = meses[meses.length - 1]!;
  const ultimoDia = new Date(ultimo.ano, ultimo.mes, 0).getDate();
  const aaaaMm = (m: { ano: number; mes: number }) => `${m.ano}-${String(m.mes).padStart(2, "0")}`;
  return { dataInicio: `${aaaaMm(primeiro)}-01`, dataFim: `${aaaaMm(ultimo)}-${String(ultimoDia).padStart(2, "0")}` };
}

export function variacaoPercentual(atual: number, original: number) {
  if (!original) return atual ? "—" : "0%";
  return `${(((atual - original) / original) * 100).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`;
}

/** Participação de `parte` sobre `total` — usada nas colunas % Original e % Participação. */
export function participacao(parte: number, total: number) {
  if (!total) return parte ? "—" : "0%";
  return `${((parte / total) * 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

/**
 * Mesma conta de `variacaoPercentual`, mas como fração (0,4523 em vez de
 * "45,23%") — para exportar em célula numérica do Excel com formato de
 * porcentagem, em vez de string. `null` onde `variacaoPercentual` mostraria
 * "—" (sem base pra comparar).
 */
export function fracaoVariacao(atual: number, original: number): number | null {
  if (!original) return atual ? null : 0;
  return (atual - original) / original;
}

/** Mesma conta de `participacao`, mas como fração — ver `fracaoVariacao`. */
export function fracaoParticipacao(parte: number, total: number): number | null {
  if (!total) return parte ? null : 0;
  return parte / total;
}

/**
 * Um nó que entra na % Contribuição exibida — sozinho (uma linha "Por loja")
 * ou como um dos vários somados numa linha agregada (Grupo, TOTAL).
 * `percEnviado` é `PERC_CONTRIBUICAO_ENVIADO` do próprio nó, já em
 * percentual (15.05 = 15,05%), `null` quando o motor caiu no fallback de
 * margem histórica (MANUAL.md §3.3, RESOLVER_PERC_CONTRIBUICAO). Negativo em
 * `contribuicao`/`calculada` remove um nó de uma soma (categorias N1 fixas
 * descontadas do total, ver `MetasManutencaoTab.tsx`).
 */
export interface ItemContribuicao {
  percEnviado: number | null;
  contribuicao: number;
  calculada: number;
}

/**
 * Contribuição efetiva (em reais) de um item: `percEnviado` aplicado à venda
 * calculada do próprio nó quando o motor resolveu um percentual — mais
 * preciso que reconstruir a partir de `contribuicao`, que já passou por
 * `reaisInteiros` — e o valor de contribuição do motor (já reflete o
 * fallback) quando não resolveu.
 */
function contribuicaoEfetiva(item: ItemContribuicao) {
  return item.percEnviado !== null ? (item.calculada * item.percEnviado) / 100 : item.contribuicao;
}

/**
 * % Contribuição de um nó (`itens` com um só elemento) ou de vários somados
 * (Grupo/TOTAL, um por loja/membro): a soma das contribuições efetivas de
 * cada item sobre a soma das vendas calculadas — uma média ponderada pela
 * venda de cada nó, não a média simples dos percentuais (loja grande pesa
 * mais que loja pequena). Usada em vez de somar `PERC_CONTRIBUICAO_ENVIADO`
 * porque o campo é por nó — não existe "percentual agregado" vindo do ERP.
 */
export function percentualContribuicao(itens: ItemContribuicao[]) {
  const calculadaTotal = itens.reduce((acc, item) => acc + item.calculada, 0);
  if (!calculadaTotal) return itens.some((item) => item.contribuicao) ? "—" : "0%";
  const efetivaTotal = itens.reduce((acc, item) => acc + contribuicaoEfetiva(item), 0);
  return `${((efetivaTotal / calculadaTotal) * 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

/** Mesma conta de `percentualContribuicao`, mas como fração — ver `fracaoParticipacao`. */
export function fracaoContribuicao(itens: ItemContribuicao[]): number | null {
  const calculadaTotal = itens.reduce((acc, item) => acc + item.calculada, 0);
  if (!calculadaTotal) return itens.some((item) => item.contribuicao) ? null : 0;
  const efetivaTotal = itens.reduce((acc, item) => acc + contribuicaoEfetiva(item), 0);
  return efetivaTotal / calculadaTotal;
}

/**
 * Converte um texto decimal (vírgula ou ponto) num inteiro na escala de
 * `casas` dígitos, ou null se inválido — assim "somar exatamente 100%" não
 * depende de aritmética de ponto flutuante. Usado pelos meses de referência
 * dos Cenários (3 casas, MetasPeriodosTab.tsx) e pelos meses anteriores dos
 * Eventos (2 casas, MetasFeriadosTab.tsx).
 */
export function paraEscala(valor: string, casas: number, permiteNegativo = false) {
  const normalizado = String(valor).trim().replace(",", ".");
  const regex = new RegExp(`^${permiteNegativo ? "-?" : ""}\\d+(\\.\\d{1,${casas}})?$`);
  if (!regex.test(normalizado)) return null;
  const total = Math.round(Number(normalizado) * 10 ** casas);
  return Number.isSafeInteger(total) ? total : null;
}

/** Busca sem diferenciar acento ou caixa. */
export function normalizar(texto: string) {
  return texto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}
